import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PetsIcon from '@mui/icons-material/Pets';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SecurityIcon from '@mui/icons-material/Security';
import MedicationIcon from '@mui/icons-material/Medication';
import NightlightIcon from '@mui/icons-material/Nightlight';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import './Home.css';

const Home: React.FC = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section 
        className="hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/photos/pets-on-holiday-hero.jpg)` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-particles">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        <motion.div 
          className="hero-content"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="hero-badge" variants={fadeInUp}>
            <PetsIcon /> Trusted Since 2017
          </motion.div>
          <motion.h1 variants={fadeInUp}>
            The Pet Nanny<br />
            <span className="highlight">of Weston</span>
          </motion.h1>
          <motion.p className="hero-subtitle" variants={fadeInUp}>
            Where Your Pets Are Treated Like Family
          </motion.p>
          <motion.p className="hero-description" variants={fadeInUp}>
            Professional, loving pet care in the comfort of a real home. 
            Your furry friends deserve the best while you're away!
          </motion.p>
          <motion.div className="hero-buttons" variants={fadeInUp}>
            <Link to="/booking" className="btn btn-primary">
              <NightlightIcon /> Book Your Stay
            </Link>
            <a href="tel:954-560-6839" className="btn btn-call">
              <PhoneIcon /> 954-560-6839
            </a>
          </motion.div>
          <motion.div className="hero-stats" variants={fadeInUp}>
            <div className="stat">
              <span className="stat-number">8+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Happy Pets</span>
            </div>
            <div className="stat">
              <span className="stat-number">5★</span>
              <span className="stat-label">Rating</span>
            </div>
          </motion.div>
        </motion.div>
        <div className="hero-scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span>Scroll Down</span>
            <div className="scroll-arrow">↓</div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <span className="section-tag">Why Us</span>
            <h2>Why Choose The Pet Nanny of Weston?</h2>
            <p className="section-description">
              We go above and beyond to ensure your pets receive the love and care they deserve
            </p>
          </motion.div>
          <motion.div 
            className="features-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><HomeIcon /></div>
              <h3>Home Environment</h3>
              <p>Your pets stay in a loving home, not a kennel. They get the comfort and attention they deserve.</p>
            </motion.div>
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><FavoriteIcon /></div>
              <h3>Personalized Care</h3>
              <p>Every pet receives individual attention and care tailored to their specific needs and personality.</p>
            </motion.div>
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><StarIcon /></div>
              <h3>Experienced & Trusted</h3>
              <p>Years of experience caring for pets of all sizes, breeds, and temperaments with love and expertise.</p>
            </motion.div>
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><CameraAltIcon /></div>
              <h3>Photo Updates</h3>
              <p>Receive regular photo updates so you can see your pet happy and having fun during their stay.</p>
            </motion.div>
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><SecurityIcon /></div>
              <h3>Safe & Secure</h3>
              <p>Fully fenced backyard with safe, supervised playtime and comfortable indoor rest areas.</p>
            </motion.div>
            <motion.div className="feature-card" variants={scaleIn} whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div className="feature-icon-wrapper"><MedicationIcon /></div>
              <h3>Medication Administration</h3>
              <p>Experienced in administering medications and following special care instructions for your pet.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="gallery-preview">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <span className="section-tag">Gallery</span>
            <h2>Happy Guests</h2>
            <p className="section-description">Some of our adorable visitors enjoying their stay!</p>
          </motion.div>
          <motion.div 
            className="gallery-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {[
              { src: "/photos/suzi-2-dogs-couch.jpg", alt: "Happy dogs on couch" },
              { src: "/photos/dog-harley.jpg", alt: "Harley the dog" },
              { src: "/photos/dog-poodle-couch.jpg", alt: "Poodle relaxing" },
              { src: "/photos/3-dogs-backyard-fence.jpg", alt: "Dogs playing in backyard" },
              { src: "/photos/dog-zorro.jpg", alt: "Zorro the dog" },
              { src: "/photos/suzi-cat-ringo.jpg", alt: "Cat Ringo" }
            ].map((photo, index) => (
              <motion.div 
                key={index}
                className="gallery-item"
                variants={scaleIn}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img src={photo.src} alt={photo.alt} />
                <div className="gallery-overlay"><PetsIcon /></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <span className="section-tag">Services</span>
            <h2>Our Services</h2>
            <p className="section-description">Comprehensive pet care tailored to your needs</p>
          </motion.div>
          <motion.div 
            className="services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <motion.div 
              className="service-card"
              variants={scaleIn}
              whileHover={{ y: -10 }}
            >
              <div className="service-icon"><VolunteerActivismIcon /></div>
              <h3>Drop-in Visits</h3>
              <p>We'll visit your home to check on your pet, provide feeding, fresh water, and some quality time.</p>
              <div className="service-price">$30<span>/visit</span></div>
              <Link to="/pricing" className="service-link">Learn More →</Link>
            </motion.div>
            <motion.div 
              className="service-card featured"
              variants={scaleIn}
              whileHover={{ y: -10 }}
            >
              <div className="service-badge">Most Popular</div>
              <div className="service-icon"><NightlightIcon /></div>
              <h3>Overnight Boarding</h3>
              <p>Your pet stays in a loving home with plenty of attention, playtime, and comfortable sleeping arrangements.</p>
              <div className="service-price">$50<span>/night</span></div>
              <Link to="/pricing" className="service-link">Learn More →</Link>
            </motion.div>
            <motion.div 
              className="service-card"
              variants={scaleIn}
              whileHover={{ y: -10 }}
            >
              <div className="service-icon"><DirectionsWalkIcon /></div>
              <h3>Doggie Daycare</h3>
              <p>Off-season fun and socialization for your pup while you're at work or running errands.</p>
              <div className="service-price">$35<span>/day</span></div>
              <Link to="/pricing" className="service-link">Learn More →</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="testimonial-content"
          >
            <div className="quote-icon">"</div>
            <p className="testimonial-text">
              Suzanne is amazing! Our dogs absolutely love staying with her. She treats them like family 
              and sends us adorable photos every day. We never worry when we travel because we know 
              they're in the best hands possible.
            </p>
            <div className="testimonial-author">
              <div className="author-info">
                <span className="author-name">Happy Pet Parent</span>
                <span className="author-location">Weston, FL</span>
              </div>
              <div className="testimonial-stars">★★★★★</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-background"></div>
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="cta-content"
          >
            <motion.h2 variants={fadeInUp}>Ready to Book Your Pet's Vacation?</motion.h2>
            <motion.p variants={fadeInUp}>Give your furry friend the care they deserve while you're away!</motion.p>
            <motion.div className="cta-buttons" variants={fadeInUp}>
              <Link to="/booking" className="btn btn-primary btn-large">
                <NightlightIcon /> Check Availability
              </Link>
              <Link to="/forms" className="btn btn-secondary btn-large">
                <DescriptionIcon /> Download Forms
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
